import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tabs,
    Tab,
    Avatar,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Cake as CakeIcon,
    Work as WorkIcon,
    Business as BusinessIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import {encryptText,encryptAppId,decryptText,decryptAppId,} from "../../utils/encryption";
import PageTitle from '../../components/PageTitle';
import AssessorSearchCalendarView from '../AssessorSearch/AssessorSearchCalendarView';


// Tab Panel component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`assessor-tabpanel-${index}`}
      aria-labelledby={`assessor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AssessorSerachProfile = () => {
  const location = useLocation();
  // Function to get orgId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessorId = searchParams.get("AssessorId");
      if (encryptedAssessorId) {
        try {
          const decryptedAssessorId = decryptText(encryptedAssessorId);
          console.log("📍 Using decrypted assessorId from URL query:", decryptedAssessorId);
          return decryptedAssessorId;
        } catch (error) {
          return encryptedAssessorId;
        }
      }
      return null;
    };

    const getTypeFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedType = searchParams.get("type");
      if (encryptedType) {
        try {
          const decryptedType = decryptText(encryptedType);
          console.log("📍 Using decrypted type from URL query:", decryptedType);
          return decryptedType;
        } catch (error) {
          return encryptedType;
        }
      }
      return null;
    };
  
    const getAssessorId = () => {
      const queryAssessorId = getAssessorIdFromQuery();
      if (queryAssessorId) {
        return queryAssessorId;
      }
      return null;
    };

    const getType = () => {
      const queryType = getTypeFromQuery();
      if (queryType) {
        return queryType;
      }
      return 'A';
    };

  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [type, setType] = useState(getType());
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessorData, setAssessorData] = useState(null);
  const [fieldsWithExpertise, setFieldsWithExpertise] = useState([]);

  // Fetch assessor profile and expertise data
  useEffect(() => {
    const fetchAssessorData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch profile data
        const profileUrl = `${process.env.REACT_APP_API_URL}/assessor/assessor-profile?assessorId=${assessorId}`;
        const profileResp = await axios.get(profileUrl);
        
        if (profileResp.data && profileResp.data.isstatus && profileResp.data.AssessorProfile.length > 0) {
          const profile = profileResp.data.AssessorProfile[0];
          
          // Parse lab types from comma-separated string
          const labTypes = profile.LabType 
            ? profile.LabType.split(',').map(type => type.trim()).filter(type => type)
            : [];
          
          // Parse standards from comma-separated string
          const standards = profile.Standards && profile.Standards !== 'N/A'
            ? profile.Standards.split(',').map(std => std.trim()).filter(std => std)
            : [];

          setAssessorData({
            id: assessorId,
            name: profile.AssessorName || '',
            mobile: profile.Mobile || '',
            email: profile.Email || '',
            address: profile.Address || '',
            dob: profile.DOB || '',
            photo: profile.Photo || '',
            expertise: labTypes,
            capacity: profile.AssessorCapacity || '',
            status: profile.WorkStatus || '',
            organization: profile.CurrentEmployer || '',
            standards: standards,
            loginName: profile.LoginName || '',
            totalAssessments: profile.TotalAssessment || 0,
            totalPre: profile.TotalPre || 0,
            totalPreOngoing: profile.TotalPreOngoing || 0,
            totalPreCompleted: profile.TotalPreCompleted || 0,
            totalMain: profile.TotalMain || 0,
            totalMainOngoing: profile.TotalMainOngoing || 0,
            totalMainCompleted: profile.TotalMainCompleted || 0,
            remarks: profile.Remarks || 'N/A'
          });
        } else {
          setError('Failed to fetch assessor profile');
        }

        // Fetch expertise data
        const expertiseUrl = `${process.env.REACT_APP_API_URL}/assessor/assessor-expertise?assessorId=${assessorId}`;
        const expertiseResp = await axios.get(expertiseUrl);
        
        if (expertiseResp.data && expertiseResp.data.isstatus && expertiseResp.data.AssessorExpertise) {
          const expertise = expertiseResp.data.AssessorExpertise.map(item => ({
            field: item.LabType || '',
            discipline: item.DisciplineName || '',
            group: item.GroupName || '',
            remarks: item.ExpertiseRemarks || 'N/A'
          }));
          setFieldsWithExpertise(expertise);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching assessor data:', err);
        setError('Unable to fetch assessor data. Please try again.');
        setLoading(false);
      }
    };

    if (assessorId) {
      fetchAssessorData();
    }
  }, [assessorId]);

  const assessmentStats = {
    total: assessorData?.totalAssessments || 0,
    pre: {
      total: assessorData?.totalPre || 0,
      ongoing: assessorData?.totalPreOngoing || 0,
      completed: assessorData?.totalPreCompleted || 0
    },
    main: {
      total: assessorData?.totalMain || 0,
      ongoing: assessorData?.totalMainOngoing || 0,
      completed: assessorData?.totalMainCompleted || 0
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getFieldColor = (field) => {
    const colors = {
      'Testing': 'primary',
      'Calibration': 'secondary',
      'Medical': 'error',
      'PTP': 'warning',
      'RMP': 'info'
    };
    return colors[field] || 'default';
  };

  return (
    <>
    <PageTitle title="NABL | Assessor Profile" />
    <Box sx={{ p: 2, mt: 10, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content - Only show when data is loaded */}
      {!loading && !error && assessorData && (
        <>
      {/* Header Section */}
      <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={assessorData.photo ? `https://nabl7t.s3.ap-south-1.amazonaws.com/live/${assessorData.photo}` : undefined}
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {!assessorData.photo && assessorData.name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {assessorData.name}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>
                  {assessorData.capacity}
                </Typography>
                <Chip 
                  label={assessorData.status} 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Mobile:</strong> {assessorData.mobile}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Email:</strong> {assessorData.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CakeIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>DOB:</strong> {assessorData.dob}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
                <Typography variant="body1">
                  <strong>Address:</strong> {assessorData.address}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Organization:</strong> {assessorData.organization}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>ID:</strong> {assessorData.loginName}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Expertise and Standards */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Expertise
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {assessorData?.expertise && assessorData.expertise.length > 0 ? (
                assessorData.expertise.map((exp, index) => (
                  <Chip 
                    key={index}
                    label={exp} 
                    color={getFieldColor(exp)}
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No expertise data available
                </Typography>
              )}
            </Box>

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Standards
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {assessorData?.standards && assessorData.standards.length > 0 ? (
                assessorData.standards.map((standard, index) => (
                  <Chip 
                    key={index}
                    label={standard} 
                    color="primary"
                    variant="filled"
                    size="small"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No standards data available
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <AssessorSearchCalendarView assessorId={assessorId}/>
  
      {/* Assessment Statistics */}
      {type!== 'assessor' && (
      <Card sx={{ mb: 2,mt: 2, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Assessment Statistics
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center'}}>
                <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                  Total Assessments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {assessmentStats.total}
                </Typography>
              </Card>
            </Grid>
            
            {/* <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f0f7ff' }}>
                <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>
                  Pre Assessment
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {assessmentStats.pre.total}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ongoing
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {assessmentStats.pre.ongoing}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {assessmentStats.pre.completed}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid> */}

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }}>
                <Typography variant="h6" color="warning.dark" sx={{ mb: 1 }}>
                  Main Assessment
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {assessmentStats.main.total}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ongoing
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {assessmentStats.main.ongoing}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {assessmentStats.main.completed}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}
      {/* Fields with Expertise */}
      {type!== 'assessor' && (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              mb: 2
            }}
          >
            <Tab label="Fields with Expertise" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{ maxHeight: '500px', overflow: 'auto' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' }}>
                      Field
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' }}>
                      Discipline/Specialization
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' }}>
                      Groups/Categorization
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' }}>
                      Remarks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldsWithExpertise.map((field, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { 
                          backgroundColor: '#fafafa' 
                        }
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={field.field} 
                          color={getFieldColor(field.field)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {field.discipline}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {field.group}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          color: field.remarks !== 'N/A' ? 'primary.main' : 'text.secondary',
                          fontStyle: field.remarks !== 'N/A' ? 'italic' : 'normal'
                        }}>
                          {field.remarks}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>
      )}
      </>
      )}
    </Box>
    </>
  );
};

export default AssessorSerachProfile;